import { useEffect, useRef, useState, useMemo } from 'react'
import { parseSessionLogs, getToolInputSummary, type ParsedLogEvent } from '@/utils/parseSessionLogs'
import { MarkdownViewer } from '@/components/MarkdownViewer'
import { Badge } from '@/components/ui'
import {
  Terminal,
  Cpu,
  Wrench,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'

interface SessionLogViewerProps {
  logs: string | null | undefined
  isRunning: boolean
}

const TOOL_COLORS: Record<string, string> = {
  Bash: 'bg-warning text-warning-foreground border-warning-foreground',
  Read: 'bg-info text-info-foreground border-info-foreground',
  Write: 'bg-purple text-purple-foreground border-purple-foreground',
  Edit: 'bg-purple text-purple-foreground border-purple-foreground',
  Glob: 'bg-success text-success-foreground border-success-foreground',
  Grep: 'bg-success text-success-foreground border-success-foreground',
  Task: 'bg-info text-info-foreground border-info-foreground',
  WebFetch: 'bg-new text-new-foreground border-new-foreground',
  WebSearch: 'bg-new text-new-foreground border-new-foreground',
}

const DEFAULT_TOOL_COLOR = 'bg-neutral text-neutral-foreground border-neutral-foreground'

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const minutes = Math.floor(ms / 60000)
  const seconds = ((ms % 60000) / 1000).toFixed(0)
  return `${minutes}m ${seconds}s`
}

function getEventParentToolUseId(event: ParsedLogEvent): string | null | undefined {
  if (event.type === 'tool_call' || event.type === 'tool_result' || event.type === 'text') {
    return event.data.parentToolUseId
  }
  return undefined
}

function CollapsibleContent({
  content,
  maxLines = 8,
}: {
  content: string
  maxLines?: number
}) {
  const [expanded, setExpanded] = useState(false)
  const lines = content.split('\n')
  const isLong = lines.length > maxLines

  const displayContent = expanded ? content : lines.slice(0, maxLines).join('\n')

  return (
    <div>
      <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all bg-muted/50 rounded p-2 overflow-x-auto">
        {displayContent}
        {isLong && !expanded && '\n...'}
      </pre>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-info-foreground hover:text-info-foreground mt-1 flex items-center gap-1"
          aria-label={expanded ? 'Show less content' : `Show all ${lines.length} lines`}
        >
          {expanded ? (
            <>
              <ChevronDown className="h-3 w-3" /> Show less
            </>
          ) : (
            <>
              <ChevronRight className="h-3 w-3" /> Show all {lines.length} lines
            </>
          )}
        </button>
      )}
    </div>
  )
}

function ToolCallBlock({
  event,
  result,
  childEvents,
  toolResultMap,
  childEventsMap,
}: {
  event: ParsedLogEvent & { type: 'tool_call' }
  result?: (ParsedLogEvent & { type: 'tool_result' }) | undefined
  childEvents?: ParsedLogEvent[]
  toolResultMap: Map<string, ParsedLogEvent & { type: 'tool_result' }>
  childEventsMap: Map<string, ParsedLogEvent[]>
}) {
  const [expanded, setExpanded] = useState(false)
  const toolName = event.data.toolName
  const colorClass = TOOL_COLORS[toolName] || DEFAULT_TOOL_COLOR
  const summary = getToolInputSummary(toolName, event.data.input)
  const hasChildren = childEvents && childEvents.length > 0
  const childToolCallCount = hasChildren ? childEvents.filter(e => e.type === 'tool_call').length : 0

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
        aria-label={`${toolName}: ${summary}`}
        aria-expanded={expanded}
      >
        <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <Badge className={`${colorClass} text-[11px] px-1.5 py-0 shrink-0`}>
          {toolName}
        </Badge>
        <span className="text-xs text-muted-foreground truncate flex-1 font-mono">
          {summary}
        </span>
        {childToolCallCount > 0 && (
          <span className="text-[11px] text-muted-foreground shrink-0">
            {childToolCallCount} tool{childToolCallCount !== 1 ? 's' : ''}
          </span>
        )}
        {event.data.durationMs !== undefined && (
          <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
            {formatDuration(event.data.durationMs)}
          </span>
        )}
        {result && !result.data.isError && (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
        )}
        {result?.data.isError && (
          <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
        )}
        {!result && (
          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin shrink-0" />
        )}
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t px-3 py-2 space-y-2">
          {hasChildren ? (
            <>
              <div className="space-y-1.5 pl-3 border-l-2 border-muted">
                {childEvents.map(childEvent => {
                  if (childEvent.type === 'tool_result') return null

                  if (childEvent.type === 'tool_call') {
                    const typedChild = childEvent as ParsedLogEvent & { type: 'tool_call' }
                    const childResult = toolResultMap.get(typedChild.data.toolUseId)
                    const grandChildEvents = childEventsMap.get(typedChild.data.toolUseId)
                    return (
                      <ToolCallBlock
                        key={childEvent.id}
                        event={typedChild}
                        result={childResult}
                        childEvents={grandChildEvents}
                        toolResultMap={toolResultMap}
                        childEventsMap={childEventsMap}
                      />
                    )
                  }

                  if (childEvent.type === 'text') {
                    return (
                      <div key={childEvent.id} className="flex items-start gap-2 py-1">
                        <MessageSquare className="h-3 w-3 text-purple-500 mt-0.5 shrink-0" />
                        <div className="prose prose-sm max-w-none flex-1 min-w-0">
                          <MarkdownViewer content={(childEvent as ParsedLogEvent & { type: 'text' }).data.text} />
                        </div>
                      </div>
                    )
                  }

                  return null
                })}
              </div>
              {result && (
                <div>
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                    {result.data.isError ? 'Error' : 'Summary'}
                  </span>
                  <div className="mt-1">
                    <CollapsibleContent content={result.data.content} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {summary && toolName !== 'Bash' && (
                <div>
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Input</span>
                  <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all bg-muted/50 rounded p-2 mt-1">
                    {JSON.stringify(event.data.input, null, 2)}
                  </pre>
                </div>
              )}
              {result && (
                <div>
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                    {result.data.isError ? 'Error' : 'Output'}
                  </span>
                  <div className="mt-1">
                    <CollapsibleContent content={result.data.content} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function SessionLogViewer({ logs, isRunning }: SessionLogViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)

  const events = useMemo(() => parseSessionLogs(logs || ''), [logs])

  // Build a map of tool_use_id -> tool_result for pairing
  const toolResultMap = useMemo(() => {
    const map = new Map<string, ParsedLogEvent & { type: 'tool_result' }>()
    for (const event of events) {
      if (event.type === 'tool_result') {
        map.set(event.data.toolUseId, event as ParsedLogEvent & { type: 'tool_result' })
      }
    }
    return map
  }, [events])

  // Build a map of parent_tool_use_id -> child events for nesting
  const childEventsMap = useMemo(() => {
    const map = new Map<string, ParsedLogEvent[]>()
    for (const event of events) {
      const parentId = getEventParentToolUseId(event)
      if (parentId) {
        const children = map.get(parentId) || []
        children.push(event)
        map.set(parentId, children)
      }
    }
    return map
  }, [events])

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (shouldAutoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [events.length, shouldAutoScroll])

  const handleScroll = () => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    // Auto-scroll if user is within 100px of bottom
    setShouldAutoScroll(scrollHeight - scrollTop - clientHeight < 100)
  }

  if (!logs) {
    return (
      <div role="status" aria-live="polite" aria-label="Waiting for logs" data-testid="session-log-waiting" className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Waiting for logs...</span>
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      data-testid="session-log-viewer"
      className="space-y-2 pr-1"
    >
      {events.map((event) => {
        // Skip tool_results — they're rendered inside their tool_call blocks
        if (event.type === 'tool_result') return null

        // Skip events that are children of a tool call (rendered nested inside parent)
        const parentId = getEventParentToolUseId(event)
        if (parentId) return null

        if (event.type === 'plain') {
          return (
            <div key={event.id} className="flex items-start gap-2 py-1">
              <Terminal className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <span className="text-xs font-mono text-muted-foreground">{event.data.text}</span>
            </div>
          )
        }

        if (event.type === 'system') {
          return (
            <div key={event.id} className="flex items-start gap-2 py-1">
              <Cpu className="h-3.5 w-3.5 text-info-foreground mt-0.5 shrink-0" />
              <span className="text-xs text-muted-foreground">
                Session initialized — model: <span className="font-medium text-foreground">{event.data.model}</span>
              </span>
            </div>
          )
        }

        if (event.type === 'tool_call') {
          const result = toolResultMap.get(event.data.toolUseId)
          const childEvents = childEventsMap.get(event.data.toolUseId)
          return (
            <ToolCallBlock
              key={event.id}
              event={event as ParsedLogEvent & { type: 'tool_call' }}
              result={result}
              childEvents={childEvents}
              toolResultMap={toolResultMap}
              childEventsMap={childEventsMap}
            />
          )
        }

        if (event.type === 'text') {
          return (
            <div key={event.id} className="flex items-start gap-2 py-1">
              <MessageSquare className="h-3.5 w-3.5 text-purple-500 mt-0.5 shrink-0" />
              <div className="prose prose-sm max-w-none flex-1 min-w-0">
                <MarkdownViewer content={event.data.text} />
              </div>
            </div>
          )
        }

        if (event.type === 'result') {
          return (
            <div key={event.id} className="border-t pt-3 mt-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Final Result</span>
                {event.data.duration && (
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(event.data.duration as number)}
                  </span>
                )}
                {event.data.cost && (
                  <span className="text-xs text-muted-foreground">
                    ${(event.data.cost as number).toFixed(4)}
                  </span>
                )}
              </div>
              <div className="prose prose-sm max-w-none">
                <MarkdownViewer content={event.data.text} />
              </div>
            </div>
          )
        }

        return null
      })}

      {isRunning && (
        <div role="status" aria-live="polite" className="flex items-center gap-2 text-muted-foreground py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span className="text-xs">Processing...</span>
        </div>
      )}
    </div>
  )
}
